'use strict';

/* globals Gryphon, get_form_value */

import { toPayloadValue } from '../../utils/lib/store-payload';

/**
 * Submit payload values.
 *
 * The submit-side half of the store payload. `toPayloadValue` — how one store
 * value becomes a payload string — lives in `utils/lib/store-payload.js`
 * (RPI-1031) and is imported, never re-implemented: `shadow-compare.js`
 * measures with the same function submit ships with, which is the whole point
 * of having one definition.
 *
 * This file is the part that decides WHICH value ships, so `events.js` (which
 * BUILDS the payload from the store) and
 * `shadow-compare.js` (which MEASURES the store against the DOM scrape) cannot
 * drift apart.
 *
 * The store is the source and the scrape is a veto — see `submitValue`. The
 * veto is the temporary half: it exists so the flip ships without changing a
 * single submitted byte, and it comes out with the scaffolding.
 *
 * It deliberately does not live in `shadow-compare.js`: that file is
 * scaffolding scheduled for deletion one clean release after the flip, and this
 * is production submit logic that outlives it.
 */

/**
 * The value to submit for one variable, and where it came from.
 *
 * The store is the SOURCE. The DOM scrape is a VETO, not a second source:
 * wherever the form disagrees with the store, the form's value ships, because
 * that is the byte the pre-flip scrape sent and the byte every downstream
 * consumer has been calibrated on for years. The payload is therefore
 * byte-identical to the pre-flip one for every variable the form can express,
 * and the store ships only where the two already agree.
 *
 * Two guards, two distinct debts, deliberately not merged:
 *
 *   - `undefined` in the store -> `dom-fallback`. Nothing ever wrote this
 *     variable. Fixed in the seed (`answer-seed.js`, `answer-sync.js`).
 *   - store value != scrape    -> `dom-override`. Something wrote it and wrote
 *     it differently. Fixed in the sync path, or it is an encoding difference.
 *
 * Both must be zero across production traffic before the veto comes out; they
 * are counted apart because they are fixed in different files. When they are,
 * deleting the scrape is a provable no-op — that is what makes this the last
 * step of the flip rather than a follow-up to it.
 *
 * NOTE: the veto SUBSUMES the old "`''` is a real answer" carve-out. A field
 * the respondent cleared whose control still reads non-empty now submits the
 * control's value. That is what legacy did, so do not reintroduce a
 * store-wins-on-empty branch without moving the exit criterion with it.
 *
 * Reading the form can never throw out of here: this is the submit path, and a
 * throw would strand the respondent. On any failure the store's value is used
 * and the reason is returned for the telemetry.
 *
 * @param {string} name Variable name, from `page_state.allvars`.
 * @returns {{value: string, source: string, error: (string|undefined)}}
 *   `source` is one of `store`, `dom-fallback`, `dom-override`; `events.js`
 *   counts the last two apart. The comparison side reads the store itself, so
 *   nothing here has to carry it.
 */
export function submitValue(name) {
  var store = Gryphon && Gryphon.answers;
  var raw = store ? store.get(name) : undefined;
  var stored = toPayloadValue(raw);

  // `get_form_value` indexes `document.forms[0]` unguarded — no form, no
  // scrape, and so nothing to veto with.
  if (!document.forms || document.forms.length === 0) {
    return { value: stored, source: 'store' };
  }

  var scraped;
  try {
    scraped = toPayloadValue(get_form_value(name));
  } catch (e) {
    return {
      value: stored,
      source: 'store',
      error: (e && e.message) || String(e),
    };
  }

  if (raw === undefined) {
    return { value: scraped, source: 'dom-fallback' };
  }

  if (scraped !== stored) {
    return { value: scraped, source: 'dom-override' };
  }

  return { value: stored, source: 'store' };
}
